import React from "react";
import { useNavigate } from "react-router-dom";

import supabase from "@/services/supabaseClient";
import {
  groupAlertsForAttention,
  hydrateAlert,
  hydratePharmacyAlert,
} from "@/utils/alertModel.js";
import {
  NOTIFICATION_PREFS_CHANGED_EVENT,
  readNotificationPrefsCache,
  resolveDeviceAlertPrefs,
} from "@/utils/notificationPreferences.js";
import {
  primeUrgentAlertTone,
  playUrgentAlertTone,
  showBrowserDeviceAlert,
  triggerUrgentHaptic,
} from "@/utils/deviceAlerts.js";

const SOUND_COOLDOWN_MS = 30_000;
const VIBRATION_COOLDOWN_MS = 30_000;
const GROUP_COOLDOWN_MS = 60_000;

function normalizeRole(value = "") {
  const role = String(value || "").trim().toLowerCase();
  return role === "doctor" ||
    role === "patient" ||
    role === "pharmacy" ||
    role === "admin"
    ? role
    : "";
}

function resolveNotificationRoute(alert = {}, role = "") {
  const directRoute = String(alert?.route || "").trim();
  if (directRoute) return directRoute;

  const alertId = String(alert?.id || "").trim();
  if (!alertId) return "";

  if (role === "doctor" || role === "patient") {
    return `/settings/${role}/notifications/${alertId}`;
  }

  if (role === "pharmacy") {
    return "/pharmacy/prescriptions";
  }

  return "";
}

function hasForegroundAccess() {
  if (typeof document === "undefined") return false;
  return document.visibilityState === "visible";
}

function shouldDeliverBrowserAlert(alert = {}, prefs = {}) {
  if (!alert?.bellEligible) return false;
  if (alert?.priority === "routine") return false;
  if (alert?.quiet_by_default) return false;
  if (!alert?.channels?.push) return false;
  if (!prefs.browserPushOn) return false;
  return !hasForegroundAccess();
}

function shouldDeliverForegroundUrgentCue(alert = {}, prefs = {}) {
  if (String(alert?.priority || "").trim() !== "urgent") return false;
  if (!hasForegroundAccess()) return false;
  return true;
}

export default function useDeviceAlerts({
  userId,
  role,
  pharmacyNotifications = [],
} = {}) {
  const nav = useNavigate();
  const stableUserId = String(userId || "").trim();
  const stableRole = normalizeRole(role);
  const deliveredRef = React.useRef(new Set());
  const recentGroupRef = React.useRef(new Map());
  const prefsRef = React.useRef(
    resolveDeviceAlertPrefs(readNotificationPrefsCache(stableRole, stableUserId), stableRole)
  );
  const interactionAtRef = React.useRef(Date.now());
  const primingRef = React.useRef(false);
  const primedRef = React.useRef(false);
  const lastSoundAtRef = React.useRef(0);
  const lastVibrationAtRef = React.useRef(0);

  React.useEffect(() => {
    prefsRef.current = resolveDeviceAlertPrefs(
      readNotificationPrefsCache(stableRole, stableUserId),
      stableRole
    );

    const refreshPrefs = () => {
      prefsRef.current = resolveDeviceAlertPrefs(
        readNotificationPrefsCache(stableRole, stableUserId),
        stableRole
      );
    };

    window.addEventListener("storage", refreshPrefs);
    window.addEventListener(NOTIFICATION_PREFS_CHANGED_EVENT, refreshPrefs);

    return () => {
      window.removeEventListener("storage", refreshPrefs);
      window.removeEventListener(NOTIFICATION_PREFS_CHANGED_EVENT, refreshPrefs);
    };
  }, [stableRole, stableUserId]);

  React.useEffect(() => {
    const noteInteraction = () => {
      interactionAtRef.current = Date.now();
      if (primedRef.current || primingRef.current) return;
      primingRef.current = true;
      primeUrgentAlertTone()
        .then((primed) => {
          if (primed) primedRef.current = true;
        })
        .finally(() => {
          primingRef.current = false;
        });
    };

    window.addEventListener("pointerdown", noteInteraction, true);
    window.addEventListener("keydown", noteInteraction, true);

    return () => {
      window.removeEventListener("pointerdown", noteInteraction, true);
      window.removeEventListener("keydown", noteInteraction, true);
    };
  }, []);

  const deliverAlert = React.useCallback(
    async (alert) => {
      if (!alert?.id || alert?.read_at) return;
      if (!alert?.bellEligible) return;

      const versionKey = `${String(alert.id)}:${String(
        alert.updated_at || alert.updatedAt || alert.created_at || alert.createdAt || ""
      )}`;

      if (deliveredRef.current.has(versionKey)) return;
      deliveredRef.current.add(versionKey);

      const groupKey = String(alert?.group_key || alert?.groupKey || "").trim();
      if (groupKey && String(alert?.priority || "") !== "urgent") {
        const previous = recentGroupRef.current.get(groupKey) || 0;
        if (Date.now() - previous < GROUP_COOLDOWN_MS) return;
        recentGroupRef.current.set(groupKey, Date.now());
      }

      const prefs = prefsRef.current || {};
      const route = resolveNotificationRoute(alert, stableRole);

      if (shouldDeliverBrowserAlert(alert, prefs)) {
        showBrowserDeviceAlert({
          title: alert.title,
          body: alert.message,
          tag: groupKey || String(alert.id),
          route,
          onNavigate: (targetRoute) => {
            if (!targetRoute) return;
            nav(targetRoute);
          },
        });
      }

      if (!shouldDeliverForegroundUrgentCue(alert, prefs)) return;

      const recentInteractionMs = Date.now() - interactionAtRef.current;
      if (recentInteractionMs > 5 * 60_000) return;

      if (alert?.channels?.sound && prefs.soundOn) {
        const now = Date.now();
        if (now - lastSoundAtRef.current >= SOUND_COOLDOWN_MS) {
          const played = await playUrgentAlertTone(prefs.messageAlertSound);
          if (played) lastSoundAtRef.current = now;
        }
      }

      if (alert?.channels?.vibration && prefs.vibrationOn) {
        const now = Date.now();
        if (now - lastVibrationAtRef.current >= VIBRATION_COOLDOWN_MS) {
          triggerUrgentHaptic();
          lastVibrationAtRef.current = now;
        }
      }
    },
    [nav, stableRole]
  );

  React.useEffect(() => {
    if (!stableUserId || !stableRole || stableRole === "pharmacy") return undefined;

    let active = true;

    async function seedCurrentRows() {
      const { data, error } = await supabase
        .from("notifications")
        .select(
          "id, title, message, type, priority, source, audience, channels, quiet_by_default, escalation_eligible, group_key, read_at, created_at, updated_at"
        )
        .eq("user_id", stableUserId)
        .order("updated_at", { ascending: false })
        .order("created_at", { ascending: false })
        .limit(250);

      if (!active || error) return;

      (Array.isArray(data) ? data : []).forEach((row) => {
        const hydrated = hydrateAlert(row, { fallbackAudience: stableRole });
        const versionKey = `${String(hydrated.id || "")}:${String(
          hydrated.updated_at || hydrated.created_at || ""
        )}`;
        if (versionKey.trim()) deliveredRef.current.add(versionKey);
      });
    }

    seedCurrentRows();

    const channel = supabase
      .channel(`device-alerts-${stableUserId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "notifications",
          filter: `user_id=eq.${stableUserId}`,
        },
        (payload) => {
          const nextRow = payload?.new || null;
          if (!nextRow || !active) return;
          deliverAlert(hydrateAlert(nextRow, { fallbackAudience: stableRole }));
        }
      )
      .subscribe();

    return () => {
      active = false;
      supabase.removeChannel(channel);
    };
  }, [deliverAlert, stableRole, stableUserId]);

  React.useEffect(() => {
    if (stableRole !== "pharmacy") return;

    const groupedAlerts = groupAlertsForAttention(
      (Array.isArray(pharmacyNotifications) ? pharmacyNotifications : [])
        .map((item) => hydratePharmacyAlert(item))
        .filter((item) => item?.bellEligible)
    );

    groupedAlerts.forEach((alert) => {
      deliverAlert(alert);
    });
  }, [deliverAlert, pharmacyNotifications, stableRole]);
}
